
import * as yup from "yup";


export enum StatusType {
    Scheduled = 1,
    Done = 2,
    Cancelled = 3,
    Rescheduled = 4,
}

export const statusTypeOptions = [
    { id: StatusType.Scheduled, name: "Scheduled" },
    { id: StatusType.Done, name: "Done" },
    { id: StatusType.Cancelled, name: "Cancelled" },
    { id: StatusType.Rescheduled, name: "Rescheduled" },
];

export const meetingDurationOptions = [
    { id: 15, name: "15 Minutes" },
    { id: 30, name: "30 Minutes" },
    { id: 45, name: "45 Minutes" },
    { id: 60, name: "60 Minutes" },
    { id: 75, name: "75 Minutes" },
    { id: 90, name: "90 Minutes" },
    { id: 105, name: "105 Minutes" },
    { id: 120, name: "120 Minutes" },
    { id: 135, name: "135 Minutes" },
    { id: 150, name: "150 Minutes" },
    { id: 165, name: "165 Minutes" },
    { id: 180, name: "180 Minutes" },
];

export const meetingFilters = [
    { id: "upcoming", name: "Upcoming" },
    { id: "all", name: "All" },
    { id: "previous", name: "Previous" },
];

export const statusFilters = [
    { id: "all", name: "All" },
    { id: "Scheduled", name: "Scheduled" },
    { id: "Done", name: "Done" },
    { id: "Cancelled", name: "Cancelled" },
    { id: "Rescheduled", name: "Rescheduled" },
];
export interface IMeeting {
    id: number;
    leadId: number;
    meetingTitle: string;
    description: string;
    meetingDate: string;
    startDateTime: string;
    endDateTime: string;
    meetingDuration: number;
    isAllDay: boolean;
    meetingStatusType: StatusType;
    remarks: string;
    createdAt: string;
    extraEmails: string[];
    createdByName: string;
    meetingStatusName?: string;
    meetingLinkType?: "googleLink" | "usingLink" | "locationVisit" | "LocationVisit" | string;
    meeting_Link?: string;
    locationId?: number;
    locationName?: string;
    locationMaster?: { id: number; locationName: string };
}

export interface IMeetingForm {
    meetingTitle: string;
    description: string;
    meetingDate: string;
    startDateTime?: string;
    endDateTime?: string;
    meetingDuration: number;
    isAllDay: boolean;
    meetingStatusType?: StatusType;
    remarks?: string;
    meetingLinkType?: string;
    meeting_Link?: string;
    locationId?: number;
}

export const defaultValues: IMeetingForm = {
    meetingTitle: "",
    description: "",
    meetingDate: "",
    startDateTime: "",
    endDateTime: "",
    meetingDuration: 15,
    isAllDay: false,
    meetingStatusType: StatusType.Scheduled,
    remarks: "",
    meetingLinkType: "googleLink",
    meeting_Link: "",
    locationId: undefined,
};
//Validation schema for meeting form

export const meetingSchema: yup.ObjectSchema<IMeetingForm> = yup.object({
    meetingTitle: yup.string().required("Title is required").test("no-whitespace", "Title cannot be empty", value => (value?.trim().length ?? 0) > 0),
    description: yup.string().required("Description is required").test("not-empty", "Description cannot be empty", (value) => {
        if (!value) return false;
        const plainText = value
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, "")
            .trim();
        return plainText.length > 0;
    }
    ),
    meetingDate: yup.string().required("Date is required"),
    isAllDay: yup.boolean().required(),
    startDateTime: yup.string().when("isAllDay", { is: false, then: (schema) => schema.required("Start Time is required"), otherwise: (schema) => schema.default(""), }),
    endDateTime: yup.string().when("isAllDay", {
        is: false, then: (schema) => schema.required("End Time is required"), otherwise: (schema) => schema.default(""),
    }),
    meetingDuration: yup.number().default(0),
    meetingStatusType: yup.mixed<StatusType>().default(StatusType.Scheduled),
    remarks: yup.string().default(""),
    locationId: yup.number().transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))
            ? undefined
            : Number(originalValue)
    ).when("meetingLinkType", {
        is: (val: any) => val === "locationVisit" || val === "LocationVisit",
        then: (schema) => schema.typeError("Location is required").required("Location is required").min(1, "Location is required"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
    meetingLinkType: yup
        .mixed<"googleLink" | "usingLink" | "locationVisit">()
        .oneOf(["googleLink", "usingLink", "locationVisit"])
        .required()
        .default("googleLink"),

    meeting_Link: yup.string().when(["meetingLinkType", "isAllDay"], {
        is: (meetingLinkType: string, isAllDay: boolean) => meetingLinkType === "usingLink" && !isAllDay,
        then: (schema) => schema.required("Meeting link is required").url("Enter a valid URL"),
        otherwise: (schema) => schema.url("Enter a valid URL").nullable().transform((v) => v === "" ? null : v),
    }),
});

export const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const isValidUrl = (url?: string) => {
    if (!url) return false;
    return url.startsWith("http://") || url.startsWith("https://");
};

export const formatMeetingDate = (date: string) => {
    if (!date) return "-";

    const [year, month, day] = date.split("T")[0].split("-");
    return `${day}-${month}-${year}`;
};

